import React, { useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Popover from "@material-ui/core/Popover";
import Switch from "@material-ui/core/Switch";
import InputBase from "@material-ui/core/InputBase";
import Tooltip from "@material-ui/core/Tooltip";
import ButtonBase from "@material-ui/core/ButtonBase";
import WhatsAppIcon from "@material-ui/icons/WhatsApp";
import SearchRoundedIcon from "@material-ui/icons/SearchRounded";
import ExpandMoreRoundedIcon from "@material-ui/icons/ExpandMoreRounded";
import WifiOffRoundedIcon from "@material-ui/icons/WifiOffRounded";
import LinkOffRoundedIcon from "@material-ui/icons/LinkOffRounded";
import AutorenewRoundedIcon from "@material-ui/icons/AutorenewRounded";
import { toast } from "react-toastify";

import api from "../../services/api";
import {
  situacaoDaConexao,
  formatarNumeroConexao,
} from "../../utils/statusConexao";

/**
 * Escolha de quais números do WhatsApp atendem com um fluxo visual.
 *
 * Regra: só aparece para escolher o que está NO AR (status CONNECTED e
 * conexão ativa). Número que caiu continua vinculado ao fluxo (chatbotId
 * não é apagado) -- ele simplesmente para de receber, então sai do fluxo na
 * prática, e volta sozinho quando reconectar. O adm pode desvincular na
 * seção "Fora do ar" se não quiser que ele volte.
 *
 * Cada chave salva na hora e mexe SÓ naquela conexão; nunca reescreve as
 * outras (antes, salvar a lista desvinculava as que estavam escondidas).
 */

export const estaNoAr = (w) => w && w.ativa !== false && w.status === "CONNECTED";

const VERDE = "#0D9488";

const useStyles = makeStyles((theme) => {
  const escuro = theme.palette.type === "dark";
  const borda = escuro ? "rgba(148,163,184,0.16)" : "#e5ebf2";
  const fundoLinha = escuro ? "rgba(15,23,42,0.6)" : "#fff";
  return {
    gatilho: {
      height: 40,
      minWidth: 250,
      maxWidth: 320,
      borderRadius: 12,
      border: `1px solid ${borda}`,
      background: escuro ? "#121c1b" : "#fff",
      padding: "0 10px 0 6px",
      display: "flex",
      alignItems: "center",
      gap: 8,
      justifyContent: "flex-start",
      transition: "border-color .15s, box-shadow .15s",
      "&:hover": {
        borderColor: VERDE,
        boxShadow: "0 0 0 3px rgba(13,148,136,0.10)",
      },
    },
    gatilhoIcone: {
      width: 28,
      height: 28,
      borderRadius: 9,
      background: "linear-gradient(135deg,#14b8a6,#0f766e)",
      color: "#fff",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      position: "relative",
      "& svg": { fontSize: 16 },
    },
    pontoVivo: {
      position: "absolute",
      right: -2,
      top: -2,
      width: 9,
      height: 9,
      borderRadius: "50%",
      background: "#2dd4bf",
      border: `2px solid ${escuro ? "#121c1b" : "#fff"}`,
      animation: "sbPontoVivo 2s ease-in-out infinite",
    },
    gatilhoTextos: {
      display: "flex",
      flexDirection: "column",
      alignItems: "flex-start",
      minWidth: 0,
      flex: 1,
      lineHeight: 1.15,
    },
    gatilhoRotulo: {
      fontSize: 10.5,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      letterSpacing: 0.2,
    },
    gatilhoValor: {
      fontSize: 13,
      fontWeight: 700,
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      maxWidth: "100%",
    },
    gatilhoAlerta: {
      fontSize: 10.5,
      fontWeight: 700,
      color: "#d97706",
      background: escuro ? "rgba(217,119,6,0.15)" : "#fff7ed",
      borderRadius: 999,
      padding: "2px 7px",
      display: "inline-flex",
      alignItems: "center",
      gap: 3,
      flexShrink: 0,
      "& svg": { fontSize: 12 },
    },
    seta: { color: theme.palette.text.secondary, fontSize: 20 },

    papel: {
      width: 380,
      maxWidth: "calc(100vw - 32px)",
      borderRadius: 16,
      overflow: "hidden",
      border: `1px solid ${borda}`,
      boxShadow: "0 24px 48px -12px rgba(2,6,23,0.35)",
      animation: "iaModalEntrar .18s ease-out",
    },
    painel: {
      display: "flex",
      flexDirection: "column",
      maxHeight: "min(560px, 75vh)",
      background: escuro ? "#0e1716" : "#f8fafc",
    },
    cabeca: {
      padding: "14px 16px 12px",
      background: "linear-gradient(135deg,#0b5e58 0%,#0f8f84 100%)",
      color: "#e2e8f0",
    },
    cabecaTitulo: { fontSize: 14.5, fontWeight: 800, color: "#fff" },
    cabecaSub: { fontSize: 12, color: "#94a3b8", marginTop: 2 },
    resumo: { display: "flex", gap: 6, marginTop: 10, flexWrap: "wrap" },
    pilula: {
      fontSize: 11,
      fontWeight: 700,
      borderRadius: 999,
      padding: "3px 9px",
      display: "inline-flex",
      alignItems: "center",
      gap: 5,
    },
    busca: {
      margin: "10px 12px 2px",
      display: "flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 10px",
      borderRadius: 10,
      border: `1px solid ${borda}`,
      background: fundoLinha,
      "& svg": { fontSize: 18, color: theme.palette.text.secondary },
      "& input": { fontSize: 13 },
    },
    lista: {
      overflowY: "auto",
      padding: "8px 12px 12px",
      display: "flex",
      flexDirection: "column",
      gap: 6,
    },
    secao: {
      fontSize: 10.5,
      fontWeight: 800,
      letterSpacing: 0.6,
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
      margin: "8px 2px 2px",
      display: "flex",
      alignItems: "center",
      gap: 6,
    },
    linha: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      padding: "8px 6px 8px 10px",
      borderRadius: 12,
      background: fundoLinha,
      border: `1px solid ${borda}`,
      transition: "border-color .15s, transform .15s, box-shadow .15s",
      animation: "iaSubir .25s ease-out both",
      "&:hover": {
        borderColor: "rgba(13,148,136,0.45)",
        boxShadow: "0 4px 14px -8px rgba(13,148,136,0.5)",
      },
    },
    linhaAtiva: {
      borderColor: "rgba(13,148,136,0.55)",
      background: escuro ? "rgba(13,148,136,0.10)" : "#effcfa",
    },
    avatar: {
      width: 34,
      height: 34,
      borderRadius: 10,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      position: "relative",
      "& svg": { fontSize: 18 },
    },
    textos: { flex: 1, minWidth: 0 },
    nome: {
      fontSize: 13.5,
      fontWeight: 700,
      color: theme.palette.text.primary,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    detalhe: {
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      display: "flex",
      alignItems: "center",
      gap: 6,
      flexWrap: "wrap",
      marginTop: 1,
    },
    etiqueta: {
      fontSize: 10.5,
      fontWeight: 700,
      borderRadius: 6,
      padding: "1px 6px",
    },
    linhaFora: {
      opacity: 0.85,
      borderStyle: "dashed",
    },
    botaoDesvincular: {
      fontSize: 11.5,
      fontWeight: 700,
      color: "#e53935",
      borderRadius: 8,
      padding: "5px 8px",
      display: "inline-flex",
      alignItems: "center",
      gap: 4,
      "& svg": { fontSize: 15 },
      "&:hover": { background: "rgba(229,57,53,0.08)" },
    },
    vazio: {
      textAlign: "center",
      fontSize: 12.5,
      color: theme.palette.text.secondary,
      padding: "18px 8px",
    },
    rodape: {
      padding: "10px 14px",
      fontSize: 11.5,
      color: theme.palette.text.secondary,
      borderTop: `1px solid ${borda}`,
      display: "flex",
      gap: 8,
      alignItems: "flex-start",
      background: escuro ? "#0e1716" : "#fff",
      "& svg": { fontSize: 16, color: VERDE, flexShrink: 0, marginTop: 1 },
    },
  };
});

/** Lista de conexões de um fluxo (usada no popover do construtor e no modal da lista de fluxos). */
export const PainelConexoes = ({ chatbotId, chatbots = [], whatsapps = [], onAtualizado, compacto }) => {
  const classes = useStyles();
  const [busca, setBusca] = useState("");
  const [salvando, setSalvando] = useState(null);
  const idFluxo = Number(chatbotId);

  const { noAr, foraDoAr } = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const casa = (w) =>
      !termo ||
      String(w.name || "").toLowerCase().includes(termo) ||
      String(w.numero || "").includes(termo.replace(/\D/g, "") || "§");

    const noAr = whatsapps
      .filter(estaNoAr)
      .filter(casa)
      .sort((a, b) => {
        const pa = a.chatbotId === idFluxo ? 0 : 1;
        const pb = b.chatbotId === idFluxo ? 0 : 1;
        return pa - pb || String(a.name).localeCompare(String(b.name));
      });

    const foraDoAr = whatsapps
      .filter((w) => !estaNoAr(w) && w.chatbotId === idFluxo)
      .filter(casa);

    return { noAr, foraDoAr };
  }, [whatsapps, busca, idFluxo]);

  const totalNoFluxo = whatsapps.filter((w) => estaNoAr(w) && w.chatbotId === idFluxo).length;
  const totalFora = whatsapps.filter((w) => !estaNoAr(w) && w.chatbotId === idFluxo).length;

  const nomeFluxo = (id) => chatbots.find((c) => c.id === id)?.name;

  const alterar = async (w, novoChatbotId, mensagem) => {
    setSalvando(w.id);
    try {
      await api.put(`/whatsapp/${w.id}`, { chatbotId: novoChatbotId });
      toast.success(mensagem);
      onAtualizado && onAtualizado(w.id, novoChatbotId);
    } catch (err) {
      toast.error("Não consegui alterar a conexão. Tente de novo.");
    } finally {
      setSalvando(null);
    }
  };

  const alternar = (w) => {
    if (w.chatbotId === idFluxo) {
      alterar(w, null, `${w.name} saiu deste fluxo.`);
    } else {
      const antes = nomeFluxo(w.chatbotId);
      alterar(
        w,
        idFluxo,
        antes ? `${w.name} saiu de "${antes}" e entrou neste fluxo.` : `${w.name} entrou neste fluxo.`
      );
    }
  };

  const etiquetaDe = (w) => {
    if (w.chatbotId === idFluxo) {
      return { texto: "Neste fluxo", cor: VERDE, fundo: "rgba(13,148,136,0.12)" };
    }
    if (w.chatbotId) {
      return { texto: `Em: ${nomeFluxo(w.chatbotId) || "outro fluxo"}`, cor: "#b45309", fundo: "rgba(245,158,11,0.14)" };
    }
    return { texto: "Sem fluxo · humano", cor: "#64748b", fundo: "rgba(100,116,139,0.12)" };
  };

  return (
    <div className={classes.painel}>
      <div className={classes.cabeca}>
        <div className={classes.cabecaTitulo}>Conexões deste fluxo</div>
        <div className={classes.cabecaSub}>Ligue os números que devem atender com este fluxo.</div>
        <div className={classes.resumo}>
          <span className={classes.pilula} style={{ background: "rgba(45,212,191,0.16)", color: "#2dd4bf" }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#2dd4bf" }} />
            {totalNoFluxo} atendendo agora
          </span>
          {totalFora > 0 && (
            <span className={classes.pilula} style={{ background: "rgba(251,191,36,0.16)", color: "#fbbf24" }}>
              <WifiOffRoundedIcon style={{ fontSize: 12 }} />
              {totalFora} fora do ar
            </span>
          )}
        </div>
      </div>

      {whatsapps.filter(estaNoAr).length > 5 && (
        <div className={classes.busca}>
          <SearchRoundedIcon />
          <InputBase
            placeholder="Buscar por nome ou número"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            fullWidth
          />
        </div>
      )}

      <div className={classes.lista} style={compacto ? { maxHeight: 420 } : undefined}>
        <div className={classes.secao}>
          <span style={{ width: 7, height: 7, borderRadius: "50%", background: VERDE }} />
          No ar ({noAr.length})
        </div>

        {noAr.length === 0 && (
          <div className={classes.vazio}>
            {busca ? "Nenhuma conexão encontrada." : "Nenhum número conectado no momento."}
          </div>
        )}

        {noAr.map((w, i) => {
          const marcado = w.chatbotId === idFluxo;
          const etq = etiquetaDe(w);
          return (
            <div
              key={w.id}
              className={`${classes.linha} ${marcado ? classes.linhaAtiva : ""}`}
              style={{ animationDelay: `${Math.min(i, 10) * 25}ms` }}
            >
              <div
                className={classes.avatar}
                style={{
                  background: marcado ? "linear-gradient(135deg,#14b8a6,#0f766e)" : "rgba(13,148,136,0.12)",
                  color: marcado ? "#fff" : VERDE,
                }}
              >
                <WhatsAppIcon />
              </div>
              <div className={classes.textos}>
                <div className={classes.nome} title={w.name}>{w.name}</div>
                <div className={classes.detalhe}>
                  {formatarNumeroConexao(w.numero) && <span>{formatarNumeroConexao(w.numero)}</span>}
                  <span className={classes.etiqueta} style={{ color: etq.cor, background: etq.fundo }}>
                    {etq.texto}
                  </span>
                </div>
              </div>
              <Tooltip title={marcado ? "Tirar deste fluxo" : w.chatbotId ? "Mover para este fluxo" : "Colocar neste fluxo"}>
                <span>
                  <Switch
                    color="primary"
                    size="small"
                    checked={marcado}
                    disabled={salvando === w.id}
                    onChange={() => alternar(w)}
                  />
                </span>
              </Tooltip>
            </div>
          );
        })}

        {foraDoAr.length > 0 && (
          <>
            <div className={classes.secao} style={{ marginTop: 12 }}>
              <WifiOffRoundedIcon style={{ fontSize: 13, color: "#d97706" }} />
              Fora do ar · voltam sozinhas ({foraDoAr.length})
            </div>
            {foraDoAr.map((w) => {
              const s = situacaoDaConexao(w.ativa === false ? "" : w.status);
              const rotulo = w.ativa === false ? "Inativo" : s.rotulo;
              const cor = w.ativa === false ? "#667085" : s.cor;
              return (
                <div key={w.id} className={`${classes.linha} ${classes.linhaFora}`}>
                  <div className={classes.avatar} style={{ background: "rgba(100,116,139,0.12)", color: "#94a3b8" }}>
                    <WhatsAppIcon />
                  </div>
                  <div className={classes.textos}>
                    <div className={classes.nome} title={w.name}>{w.name}</div>
                    <div className={classes.detalhe}>
                      <span className={classes.etiqueta} style={{ color: cor, background: `${cor}1F` }}>
                        {rotulo}
                      </span>
                      <span>volta ao fluxo ao reconectar</span>
                    </div>
                  </div>
                  <Tooltip title="Desvincular: não volta mais para este fluxo sozinho">
                    <ButtonBase
                      className={classes.botaoDesvincular}
                      disabled={salvando === w.id}
                      onClick={() => alterar(w, null, `${w.name} foi desvinculada deste fluxo.`)}
                    >
                      <LinkOffRoundedIcon />
                      Tirar
                    </ButtonBase>
                  </Tooltip>
                </div>
              );
            })}
          </>
        )}
      </div>

      <div className={classes.rodape}>
        <AutorenewRoundedIcon />
        <span>
          Número que cai sai do fluxo na hora e volta sozinho quando reconectar. Só aparecem aqui para escolher os números conectados.
        </span>
      </div>
    </div>
  );
};

/** Botão compacto + popover (barra do construtor). */
const SeletorConexoes = ({ chatbotId, chatbots, whatsapps, onAtualizado, className }) => {
  const classes = useStyles();
  const [ancora, setAncora] = useState(null);
  const idFluxo = Number(chatbotId);

  const noFluxo = whatsapps.filter((w) => estaNoAr(w) && w.chatbotId === idFluxo);
  const fora = whatsapps.filter((w) => !estaNoAr(w) && w.chatbotId === idFluxo).length;

  const valor =
    noFluxo.length === 0
      ? "Nenhum número atendendo"
      : noFluxo.length === 1
      ? noFluxo[0].name
      : `${noFluxo.length} números atendendo`;

  return (
    <>
      <ButtonBase className={`${classes.gatilho} ${className || ""}`} onClick={(e) => setAncora(e.currentTarget)}>
        <span className={classes.gatilhoIcone}>
          <WhatsAppIcon />
          {noFluxo.length > 0 && <span className={classes.pontoVivo} />}
        </span>
        <span className={classes.gatilhoTextos}>
          <span className={classes.gatilhoRotulo}>Conexões (WhatsApp)</span>
          <span className={classes.gatilhoValor}>{valor}</span>
        </span>
        {fora > 0 && (
          <Tooltip title={`${fora} vinculada(s) fora do ar — voltam sozinhas ao reconectar`}>
            <span className={classes.gatilhoAlerta}>
              <WifiOffRoundedIcon />
              {fora}
            </span>
          </Tooltip>
        )}
        <ExpandMoreRoundedIcon className={classes.seta} />
      </ButtonBase>

      <Popover
        open={Boolean(ancora)}
        anchorEl={ancora}
        onClose={() => setAncora(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{ className: classes.papel, style: { marginTop: 8 } }}
      >
        <PainelConexoes
          chatbotId={chatbotId}
          chatbots={chatbots}
          whatsapps={whatsapps}
          onAtualizado={onAtualizado}
        />
      </Popover>
    </>
  );
};

export default SeletorConexoes;
